import React, { useEffect, useState } from 'react';
import { createApiUrl } from '../config/api';
import { useApiAuth } from '../hooks/useApiAuth';
import type { Session } from '../utils/sessionUtils';
import {
  formatDate,
  getTimeAgo,
  scrollbarStyles,
  sortSessionsByLastActive
} from '../utils/sessionUtils';

interface SessionsOverviewProps {
  userID: string;
  onSessionSelected: (sessionId: string, shouldGoToChat: boolean, shouldLoadFromBackend?: boolean) => void;
}

const SessionsOverview: React.FC<SessionsOverviewProps> = ({
  userID,
  onSessionSelected,
}) => {
  const { makeAuthenticatedRequest } = useApiAuth();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [creatingSession, setCreatingSession] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createNewSessionAndNavigate = async () => {
    try {
      // No need to send user_id - it comes from the auth token!
      const response = await makeAuthenticatedRequest(createApiUrl("/session/initialize"), {
        method: "POST"
      });
      
      if (response.ok) {
        const data = await response.json();
        onSessionSelected(data.session_id, false, false); // false = don't load from backend for new session
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error creating session:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        // No need to send user_id in URL - backend gets it from auth token!
        const response = await makeAuthenticatedRequest(createApiUrl(`/session/sessions_lean_by_user`));
        
        if (response.status === 404) {
          // No sessions found, create a new one automatically
          await createNewSessionAndNavigate();
        } else if (response.ok) {
          const data = await response.json();
          if (data.length === 0) {
            // Empty sessions array, create a new one automatically
            await createNewSessionAndNavigate();
          } else {
            // Sort sessions by last_active date, most recent first
            const sortedSessions = sortSessionsByLastActive(data);
            setSessions(sortedSessions);
            setLoading(false);
          }
        } else {
          setError(`Failed to load sessions: ${response.status} ${response.statusText}`);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching sessions:', err);
        setError('Unable to connect to the server. Please check your internet connection and try again.');
        setLoading(false);
      }
    };

    if (userID) {
      fetchSessions();
    }
  }, [userID, onSessionSelected]);

  const handleCreateNewSession = async () => {
    setCreatingSession(true);
    await createNewSessionAndNavigate();
    setCreatingSession(false);
  };

  const handleResumeSession = async (sessionId: string) => {
    try {
      const response = await makeAuthenticatedRequest(createApiUrl(`/session/resume/${sessionId}`));
      
      if (response.ok) {
        const sessionData = await response.json();
        const shouldGoToChat = sessionData.chat_data && 
                              sessionData.chat_data.messages && 
                              sessionData.chat_data.messages.length > 0;
        onSessionSelected(sessionId, shouldGoToChat, true); // true = load from backend for resume
      }
    } catch (error) {
      console.error('Error resuming case:', error);
    }
  };

  const handleDeleteSession = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent session resume when clicking delete
    try {
      const response = await makeAuthenticatedRequest(createApiUrl('/session/delete'), {
        method: 'DELETE',
        body: JSON.stringify({ session_id: sessionId })
      });
      
      if (response.ok) {
        // Remove the session from the local state
        setSessions(sessions => sessions.filter(s => s.id !== sessionId));
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <style>{scrollbarStyles}</style>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your cases...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <style>{scrollbarStyles}</style>
        <div className="text-center max-w-md mx-auto">
          <div className="mb-6">
            <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.962-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      <style>{scrollbarStyles}</style>
      <div className="w-full px-8 py-8 flex flex-col h-full">
        {/* Header with Logo */}
        <div className="mb-8 text-center flex-shrink-0">
          <div className="mb-6">
            <img 
              src="/logo_blue_full.webp" 
              alt="Enablai Logo" 
              className="h-16 mx-auto"
              loading="eager"
              decoding="async"
            />
          </div>
        </div>

        <div className="mb-8 flex-shrink-0">
          <button
            onClick={handleCreateNewSession}
            disabled={creatingSession}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-3 rounded-lg font-medium inline-flex items-center gap-2 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {creatingSession ? 'Creating...' : 'Start New Case'}
          </button>
        </div>

        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto scrollbar-on-scroll">
            <div className="space-y-4 pb-8">
              {sessions.map((session) => (
              <div
                key={session.id}
                className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer w-full"
                onClick={() => handleResumeSession(session.id)}
              >
                <div className="flex items-start justify-between w-full">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-medium text-gray-900">
                        {session.title || `Session ${session.id.slice(0, 8)}...`}
                      </h3>
                      {session.is_active && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-6 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Created {formatDate(session.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Last active {getTimeAgo(session.last_active)}
                      </span>
                      <button
                        onClick={(e) => handleDeleteSession(session.id, e)}
                        className="flex items-center gap-1 text-red-500 hover:text-red-700 transition-colors"
                        title="Delete case"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center ml-6">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionsOverview;